import { IS_DEV } from '@/config/env';

/** What a write does to one row of a list. */
export type PendingKind = 'create' | 'update' | 'delete';

/** Every row a live list shows. A marked row waits on a write of this device. */
export interface LiveRow {
  id: string;
  pendingWrite?: PendingKind;
}

/** How long a row stays marked before the list shows live data alone. */
const TIMEOUT_MS = 30_000;

/** Where the marks outlive one page load. */
const STORAGE_KEY = 'edify_pending_writes';

/** One write this device made, and what the list showed before it. */
export interface PendingEntry {
  scope: string;
  id: string;
  kind: PendingKind;
  /** The row to show until the write lands. Null for a delete. */
  row: unknown;
  /** The live row when the write started, as text. Null when there was none. */
  before: string | null;
  /** False until a list has reported its rows for this scope. */
  hasBefore: boolean;
  /** When the mark comes off, whatever the stored data holds by then. */
  expiresAt: number;
}

const NO_ROWS: readonly never[] = [];

let entries: readonly PendingEntry[] = [];
const listeners = new Set<() => void>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();

/** The rows each list showed last. */
const lastRows = new Map<string, Map<string, unknown>>();

/** The name a list and the writes that change it share. */
export function scopeOf(queryKey: readonly unknown[]): string {
  return JSON.stringify(queryKey);
}

/** One row without its mark, as the stored data holds it. */
export function unmarked<T extends LiveRow>(row: T): T {
  if (!row.pendingWrite) return row;
  const copy = { ...row };
  delete copy.pendingWrite;
  return copy;
}

function entryKey(scope: string, id: string): string {
  return `${scope}\u0000${id}`;
}

function emit(): void {
  for (const listener of [...listeners]) listener();
}

function isEntry(value: unknown): value is PendingEntry {
  const entry = value as PendingEntry | null;
  return (
    typeof entry?.scope === 'string' &&
    typeof entry?.id === 'string' &&
    typeof entry?.expiresAt === 'number'
  );
}

/** The marks a former page load left, without the ones that ran out. */
function stored(): readonly PendingEntry[] {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    if (!Array.isArray(raw)) return [];
    return raw.filter(isEntry).filter((entry) => entry.expiresAt > Date.now());
  } catch {
    return [];
  }
}

function persist(): void {
  try {
    if (entries.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // The marks then last for this page load alone.
  }
}

/** Says in development that no list confirmed one write. */
function reportUnconfirmed(entry: PendingEntry): void {
  if (!IS_DEV) return;
  const seen = lastRows.has(entry.scope) ? 'the list showed other rows' : 'no list showed this scope';
  console.warn(
    `Nothing confirmed the ${entry.kind} of "${entry.id}" in ${TIMEOUT_MS} ms: ${seen}.`,
    entry.scope,
  );
}

function forget(scope: string, id: string): void {
  const key = entryKey(scope, id);
  const timer = timers.get(key);
  if (timer) clearTimeout(timer);
  timers.delete(key);
  entries = entries.filter((entry) => entry.scope !== scope || entry.id !== id);
}

function schedule(entry: PendingEntry): void {
  timers.set(
    entryKey(entry.scope, entry.id),
    setTimeout(
      () => {
        reportUnconfirmed(entry);
        forget(entry.scope, entry.id);
        persist();
        emit();
      },
      Math.max(0, entry.expiresAt - Date.now()),
    ),
  );
}

export interface PendingWrite {
  scope: string;
  id: string;
  kind: PendingKind;
  row?: unknown;
}

/** Marks one row this device wrote, until the stored data shows the same row. */
export function markPendingWrite({ scope, id, kind, row = null }: PendingWrite): void {
  forget(scope, id);
  const seen = lastRows.get(scope);
  const entry: PendingEntry = {
    scope,
    id,
    kind,
    row,
    before: seen?.has(id) ? JSON.stringify(seen.get(id)) : null,
    hasBefore: seen !== undefined,
    expiresAt: Date.now() + TIMEOUT_MS,
  };
  entries = [...entries, entry];
  schedule(entry);
  persist();
  emit();
}

/** Clears every mark, here and in the browser's store. */
export function resetPendingWrites(): void {
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
  lastRows.clear();
  entries = [];
  persist();
  emit();
}

/** Whether the live rows now hold what the write asked for. */
function isSettled(entry: PendingEntry, live: unknown): boolean {
  if (entry.kind === 'create') return live !== undefined;
  if (live === undefined) return true;
  const text = JSON.stringify(live);
  return text !== entry.before || text === JSON.stringify(entry.row);
}

/** Takes the rows one list shows now and clears the marks those rows confirm. */
export function observeRows(scope: string, rows: readonly LiveRow[]): void {
  const seen = new Map<string, unknown>(rows.map((row) => [row.id, row]));
  lastRows.set(scope, seen);

  let settled = false;
  for (const entry of entries) {
    if (entry.scope !== scope) continue;
    if (!entry.hasBefore) {
      const before = seen.has(entry.id) ? JSON.stringify(seen.get(entry.id)) : null;
      entries = entries.map((one) => (one === entry ? { ...one, before, hasBefore: true } : one));
      settled = true;
      continue;
    }
    if (!isSettled(entry, seen.get(entry.id))) continue;
    forget(scope, entry.id);
    settled = true;
  }
  if (settled) {
    persist();
    emit();
  }
}

/** Calls the function on each change of the marks. Returns the function that stops it. */
export function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

/** Returns every mark this device holds now. */
export function snapshot(): readonly PendingEntry[] {
  return entries;
}

/** Takes on what another tab left in the store. */
function adopt(): void {
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
  entries = stored();
  entries.forEach(schedule);
  emit();
}

if (typeof window !== 'undefined') {
  adopt();
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) adopt();
  });
}

export { NO_ROWS };
